import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TOOLTIP_DELAY_DURATION_MS } from '../../constants';
import { clampTooltipPosition } from '../../utils/clamp-tooltip-position';
import {
  InteractiveTooltipApi,
  InteractiveTooltipPortal,
  useInteractiveTooltip,
} from './interactive-tooltip';

export enum TooltipVariant {
  Default = 'default',
  StateChange = 'stateChange',
}

export enum TooltipAlign {
  Start = 'start',
  Center = 'center',
  End = 'end',
}

interface TooltipData {
  content: ReactNode;
  x: number;
  y: number;
  variant: TooltipVariant;
  align: TooltipAlign;
  alignOffset: number;
}

interface PlannerTooltipContextValue {
  /** Show a simple non-interactive tooltip (e.g. utilization cells, state changes) */
  showTooltip: (
    content: ReactNode,
    x: number,
    y: number,
    variant?: TooltipVariant,
    align?: TooltipAlign,
    alignOffset?: number
  ) => void;
  hideTooltip: () => void;
  /** Interactive tooltip API for assignment tooltips with action buttons */
  interactiveTooltip: InteractiveTooltipApi;
}

const PlannerTooltipContext = createContext<PlannerTooltipContextValue | null>(null);

export const usePlannerTooltip = () => {
  const context = useContext(PlannerTooltipContext);
  if (!context) {
    throw new Error('usePlannerTooltip must be used within PlannerTooltipProvider');
  }
  return context;
};

// Minimum distance the simple tooltip keeps from every viewport edge.
const SIMPLE_TOOLTIP_VIEWPORT_PADDING = 8;
// Vertical offset of the Default (above-cursor) tooltip from the anchor point.
const SIMPLE_TOOLTIP_OFFSET_Y = 40;

export const PlannerTooltipProvider = ({ children }: { children: ReactNode }) => {
  // === Simple (non-interactive) tooltip state ===
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Measure the simple tooltip so we can clamp it into the viewport. Measured on
  // each data change before the fade-in completes (it animates opacity only).
  useEffect(() => {
    if (tooltipRef.current && tooltipData) {
      setTooltipSize({
        width: tooltipRef.current.offsetWidth,
        height: tooltipRef.current.offsetHeight,
      });
    }
  }, [tooltipData]);

  // === Interactive tooltip (extracted) ===
  const { api: interactiveTooltip, portalProps: interactivePortalProps } =
    useInteractiveTooltip(isScrollingRef);

  // === Simple tooltip methods ===
  const showTooltip = useCallback(
    (
      content: ReactNode,
      x: number,
      y: number,
      variant: TooltipVariant = TooltipVariant.Default,
      align: TooltipAlign = TooltipAlign.Center,
      alignOffset: number = 0
    ) => {
      // Don't show tooltips while scrolling
      if (isScrollingRef.current) return;

      // Clear any pending show timeout
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }

      // Update tooltip data immediately
      setTooltipData({ content, x, y, variant, align, alignOffset });

      // Schedule tooltip to show after delay
      showTimeoutRef.current = setTimeout(() => {
        if (!isScrollingRef.current) {
          setIsVisible(true);
        }
        showTimeoutRef.current = null;
      }, TOOLTIP_DELAY_DURATION_MS);
    },
    []
  );

  const hideTooltip = useCallback(() => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    // Hide tooltip immediately
    setIsVisible(false);
    setTooltipData(null);
  }, []);

  // Keep refs so the scroll handler doesn't need to re-attach
  const hideInteractiveImmediateRef = useRef(interactiveTooltip.hideImmediate);
  hideInteractiveImmediateRef.current = interactiveTooltip.hideImmediate;
  const updateInteractivePositionRef = useRef(interactiveTooltip.updatePosition);
  updateInteractivePositionRef.current = interactiveTooltip.updatePosition;
  const isInteractivePinnedRef = useRef(interactiveTooltip.isPinned);
  isInteractivePinnedRef.current = interactiveTooltip.isPinned;

  // === Scroll detection: hide all tooltips on scroll ===
  useEffect(() => {
    const plannerContainer = document.getElementById('planner-container');
    if (!plannerContainer) return;

    const handleScroll = () => {
      isScrollingRef.current = true;

      // Hide simple tooltip
      setIsVisible(false);

      // When the interactive tooltip is pinned (e.g. cut mode), keep it visible
      // but update its position to follow the scrolled assignment
      if (isInteractivePinnedRef.current) {
        updateInteractivePositionRef.current();
      } else {
        hideInteractiveImmediateRef.current();
      }

      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
        scrollTimeoutRef.current = null;
      }, 150);
    };

    plannerContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      plannerContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
    };
  }, []);

  // === Context value ===
  const contextValue = useMemo(
    () => ({
      showTooltip,
      hideTooltip,
      interactiveTooltip,
    }),
    [showTooltip, hideTooltip, interactiveTooltip]
  );

  // === Simple tooltip positioning ===
  const tooltipStyles = useMemo(() => {
    if (!tooltipData) return {};

    const { x, y, variant, align, alignOffset } = tooltipData;
    const { width, height } = tooltipSize;

    // Slide-in animation offsets (kept as a transform; alignment is NOT here).
    const slideOffsetY = isVisible ? '0px' : '16px';
    const slideOffsetX = isVisible ? '0px' : '-16px';

    const measured = width > 0 && height > 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (variant === TooltipVariant.StateChange) {
      // Appears to the right of the anchor, vertically centered, slides in from the
      // left. Resolve the top-left corner then clamp into the viewport.
      const rawLeft = x + 20;
      const rawTop = y - height / 2;
      const left = measured
        ? Math.max(
            SIMPLE_TOOLTIP_VIEWPORT_PADDING,
            Math.min(rawLeft, vw - width - SIMPLE_TOOLTIP_VIEWPORT_PADDING)
          )
        : rawLeft;
      const top = measured
        ? Math.max(
            SIMPLE_TOOLTIP_VIEWPORT_PADDING,
            Math.min(rawTop, vh - height - SIMPLE_TOOLTIP_VIEWPORT_PADDING)
          )
        : rawTop;
      return {
        position: 'fixed' as const,
        left,
        top,
        transform: `translateX(${slideOffsetX})`,
        pointerEvents: 'none' as const,
        transition: 'opacity 75ms, transform 75ms',
      };
    }

    // Default: appears above the anchor point, slides bottom-to-top. Resolve the
    // left edge per alignment (start = at anchor+offset, center = anchor centered,
    // end = right edge at anchor), then clamp horizontally and resolve vertical
    // with the shared helper (flips below when there's no room above).
    let rawLeft = x;
    if (measured) {
      if (align === TooltipAlign.Start) rawLeft = x + alignOffset;
      else if (align === TooltipAlign.End) rawLeft = x - width;
      else rawLeft = x - width / 2; // Center
    } else if (align === TooltipAlign.Start) {
      rawLeft = x + alignOffset;
    }

    if (!measured) {
      return {
        position: 'fixed' as const,
        left: rawLeft,
        top: y - SIMPLE_TOOLTIP_OFFSET_Y,
        transform: `translateY(${slideOffsetY})`,
        pointerEvents: 'none' as const,
        transition: 'opacity 75ms, transform 75ms',
      };
    }

    // Treat the anchor as a zero-height point; "above by OFFSET_Y" maps to a gap
    // of OFFSET_Y from the anchor when placing above.
    const { left, top } = clampTooltipPosition({
      triggerRect: { top: y, bottom: y, left: x },
      desiredLeft: rawLeft,
      tooltipWidth: width,
      tooltipHeight: height,
      gap: SIMPLE_TOOLTIP_OFFSET_Y,
      viewport: { width: vw, height: vh },
      padding: SIMPLE_TOOLTIP_VIEWPORT_PADDING,
    });

    return {
      position: 'fixed' as const,
      left,
      top,
      transform: `translateY(${slideOffsetY})`,
      pointerEvents: 'none' as const,
      transition: 'opacity 75ms, transform 75ms',
    };
  }, [tooltipData, isVisible, tooltipSize]);

  const tooltipClassName = useMemo(() => {
    if (!tooltipData) return '';

    const { variant } = tooltipData;

    const baseClasses = 'z-50 overflow-hidden rounded-md shadow-md';
    const animationClasses = isVisible ? 'opacity-100' : 'opacity-0';

    if (variant === TooltipVariant.StateChange) {
      return `${baseClasses} bg-primary text-primary-foreground max-w-xs ${animationClasses}`;
    }

    return `${baseClasses} bg-primary text-primary-foreground px-3 py-1.5 text-sm ${animationClasses}`;
  }, [tooltipData, isVisible]);

  return (
    <PlannerTooltipContext.Provider value={contextValue}>
      {children}

      {/* Simple (non-interactive) tooltip */}
      {tooltipData && (
        <div ref={tooltipRef} className={tooltipClassName} style={tooltipStyles}>
          {tooltipData.content}
        </div>
      )}

      {/* Interactive tooltip (assignment actions) */}
      <InteractiveTooltipPortal {...interactivePortalProps} />
    </PlannerTooltipContext.Provider>
  );
};
